import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { CheckCircle2, Clock, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox, Textarea } from '@/components/ui/misc';
import { formatDate, formatMoney } from '@/lib/format';
import type { ApiSuccess } from '@/lib/types';
import type { Quote } from './types';

type PublicQuote = Omit<Quote, 'publicToken' | 'ownerId' | 'owner'> & {
  expired: boolean;
  canRespond: boolean;
  issuer: {
    name: string;
    address: string | null;
    email: string | null;
    phone: string | null;
    website: string | null;
  };
};

/**
 * The client-facing quote. Deliberately outside the app shell and outside auth:
 * the token in the URL is the credential, so this page never assumes a session.
 */
export function PublicQuotePage() {
  const { token } = useParams<{ token: string }>();
  const queryClient = useQueryClient();
  const base = import.meta.env.VITE_API_URL ?? '/api/v1';

  const { data, isLoading, error } = useQuery({
    queryKey: ['public-quote', token],
    queryFn: async () => {
      const response = await axios.get<ApiSuccess<PublicQuote>>(`${base}/public/quotes/${token}`);
      return response.data.data;
    },
    retry: false,
  });

  const respond = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const response = await axios.post<ApiSuccess<{ status: string }>>(
        `${base}/public/quotes/${token}/respond`,
        body,
      );
      return response.data.data;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['public-quote', token] }),
  });

  const [decision, setDecision] = useState<'accept' | 'reject' | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [excluded, setExcluded] = useState<string[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  if (isLoading) {
    return <Shell>Loading…</Shell>;
  }

  if (error || !data) {
    return (
      <Shell>
        <div className="text-center">
          <XCircle className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <h1 className="text-lg font-semibold">This link is not valid</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            It may have been withdrawn. Please ask your contact for a new one.
          </p>
        </div>
      </Shell>
    );
  }

  const quote = data;
  const selectedOptional = (item: { id?: string; isOptional: boolean }) =>
    !item.isOptional || !excluded.includes(item.id ?? '');

  const visibleTotal = quote.items
    .filter((item) => selectedOptional(item))
    .reduce((sum, item) => sum + Number(item.lineTotal ?? 0), 0);

  const sections = groupBySection(quote.items);

  const submit = async () => {
    setFormError(null);
    if (name.trim().length < 2) {
      setFormError('Please type your full name.');
      return;
    }
    if (decision === 'reject' && reason.trim().length === 0) {
      setFormError('Please tell us why, so we can improve the proposal.');
      return;
    }

    try {
      await respond.mutateAsync({
        decision,
        name,
        email,
        reason,
        selectedItemIds: quote.items
          .filter((item) => selectedOptional(item))
          .map((item) => item.id)
          .filter(Boolean),
      });
      setDecision(null);
    } catch (caught) {
      setFormError(
        axios.isAxiosError(caught) && caught.response?.data?.error?.message
          ? String(caught.response.data.error.message)
          : 'Could not record your response. Please try again.',
      );
    }
  };

  return (
    <Shell>
      <header className="mb-8 flex flex-wrap items-start justify-between gap-4 border-b pb-6">
        <div>
          <h1 className="text-xl font-semibold">{quote.issuer.name}</h1>
          {quote.issuer.address && (
            <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">
              {quote.issuer.address}
            </p>
          )}
          <p className="mt-1 text-sm text-muted-foreground">
            {[quote.issuer.email, quote.issuer.phone].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="text-right">
          <div className="text-sm font-medium">
            Quote {quote.number}
            {quote.version > 1 && <span className="text-muted-foreground"> v{quote.version}</span>}
          </div>
          <div className="text-sm text-muted-foreground">Issued {formatDate(quote.issueDate)}</div>
          {quote.validUntil && (
            <div className="text-sm text-muted-foreground">
              Valid until {formatDate(quote.validUntil)}
            </div>
          )}
        </div>
      </header>

      <div className="mb-6">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">Prepared for</div>
        <div className="mt-1 font-medium">{quote.company.name}</div>
        {quote.contact && (
          <div className="text-sm text-muted-foreground">
            {[quote.contact.firstName, quote.contact.lastName].filter(Boolean).join(' ')}
          </div>
        )}
      </div>

      {quote.acceptedAt && (
        <Banner tone="success" icon={<CheckCircle2 className="h-4 w-4" />}>
          Accepted by {quote.acceptedByName} on {formatDate(quote.acceptedAt)}. Thank you — we will
          be in touch shortly.
        </Banner>
      )}
      {quote.rejectedAt && (
        <Banner tone="muted" icon={<XCircle className="h-4 w-4" />}>
          You declined this quote on {formatDate(quote.rejectedAt)}.
        </Banner>
      )}
      {quote.expired && !quote.acceptedAt && (
        <Banner tone="warning" icon={<Clock className="h-4 w-4" />}>
          This quote expired on {formatDate(quote.validUntil)}. Ask your contact for a fresh one.
        </Banner>
      )}

      <div className="space-y-6">
        {sections.map(([section, sectionItems]) => (
          <div key={section || 'default'}>
            {section && <h2 className="mb-2 text-sm font-semibold">{section}</h2>}
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Description</th>
                    <th className="px-3 py-2 text-right font-medium">Qty</th>
                    <th className="px-3 py-2 text-right font-medium">Unit price</th>
                    <th className="px-3 py-2 text-right font-medium">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {sectionItems.map((item) => (
                    <tr key={item.id} className="border-b last:border-0">
                      <td className="px-3 py-2.5">
                        <div className="flex items-start gap-2">
                          {item.isOptional && quote.canRespond && (
                            <Checkbox
                              className="mt-0.5"
                              checked={selectedOptional(item)}
                              onCheckedChange={(checked) =>
                                setExcluded((current) =>
                                  checked === true
                                    ? current.filter((id) => id !== item.id)
                                    : [...current, item.id ?? ''],
                                )
                              }
                            />
                          )}
                          <span>
                            {item.description}
                            {item.isOptional && (
                              <span className="ml-1.5 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                                Optional
                              </span>
                            )}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right tabular">
                        {item.quantity}
                        {item.unit ? ` ${item.unit}` : ''}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular">
                        {formatMoney(item.unitPrice, quote.currency)}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular">
                        {selectedOptional(item)
                          ? formatMoney(item.lineTotal, quote.currency)
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 flex justify-end">
        <dl className="w-full max-w-xs space-y-1.5 text-sm">
          <Row label="Subtotal" value={formatMoney(quote.subtotal, quote.currency)} />
          <Row label="Discount" value={`− ${formatMoney(quote.discountTotal, quote.currency)}`} />
          <Row label="Tax" value={formatMoney(quote.taxTotal, quote.currency)} />
          <div className="flex items-center justify-between border-t pt-2 text-base font-semibold">
            <dt>Total</dt>
            <dd className="tabular">
              {excluded.length > 0
                ? formatMoney(visibleTotal.toFixed(2), quote.currency)
                : formatMoney(quote.grandTotal, quote.currency)}
            </dd>
          </div>
        </dl>
      </div>

      {(quote.assumptions || quote.exclusions || quote.terms) && (
        <div className="mt-8 space-y-4 border-t pt-6 text-sm">
          {quote.assumptions && <Block title="Assumptions" body={quote.assumptions} />}
          {quote.exclusions && <Block title="Not included" body={quote.exclusions} />}
          {quote.terms && <Block title="Terms and conditions" body={quote.terms} />}
        </div>
      )}

      {quote.canRespond && (
        <div className="mt-8 rounded-lg border bg-muted/30 p-5">
          {decision === null ? (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium">Ready to proceed?</span>
              <Button onClick={() => setDecision('accept')}>Accept this quote</Button>
              <Button variant="outline" onClick={() => setDecision('reject')}>
                Decline
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <h2 className="text-sm font-semibold">
                {decision === 'accept' ? 'Accept this quote' : 'Decline this quote'}
              </h2>

              {decision === 'accept' && (
                <p className="text-sm text-muted-foreground">
                  Typing your name below acts as your signature. We record the date, time and your
                  IP address alongside it.
                </p>
              )}

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Your full name</Label>
                  <Input value={name} onChange={(event) => setName(event.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </div>
              </div>

              {decision === 'reject' && (
                <div className="space-y-1.5">
                  <Label>Why are you declining?</Label>
                  <Textarea
                    rows={3}
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                  />
                </div>
              )}

              {formError && <p className="text-sm text-destructive">{formError}</p>}

              <div className="flex gap-2">
                <Button
                  onClick={submit}
                  disabled={respond.isPending}
                  variant={decision === 'reject' ? 'destructive' : 'default'}
                >
                  {respond.isPending
                    ? 'Submitting…'
                    : decision === 'accept'
                      ? 'Accept'
                      : 'Decline'}
                </Button>
                <Button variant="ghost" onClick={() => setDecision(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-muted/40 px-4 py-10">
      <div className="mx-auto max-w-3xl rounded-lg border bg-background p-6 shadow-sm sm:p-10">
        {children}
      </div>
    </div>
  );
}

function Banner({
  tone,
  icon,
  children,
}: {
  tone: 'success' | 'warning' | 'muted';
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  const styles = {
    success: 'border-emerald-300 bg-emerald-50 text-emerald-900',
    warning: 'border-amber-300 bg-amber-50 text-amber-900',
    muted: 'border-border bg-muted text-muted-foreground',
  }[tone];

  return (
    <div className={`mb-6 flex items-start gap-2 rounded-md border px-4 py-3 text-sm ${styles}`}>
      <span className="mt-0.5 shrink-0">{icon}</span>
      <span>{children}</span>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="tabular">{value}</dd>
    </div>
  );
}

function Block({ title, body }: { title: string; body: string }) {
  return (
    <div>
      <h3 className="mb-1 font-medium">{title}</h3>
      <p className="whitespace-pre-wrap text-muted-foreground">{body}</p>
    </div>
  );
}

/** Keeps a section's lines together, with unsectioned lines first. */
function groupBySection(items: Quote['items']): [string, Quote['items']][] {
  const groups = new Map<string, Quote['items']>();
  for (const item of items) {
    const key = item.section ?? '';
    groups.set(key, [...(groups.get(key) ?? []), item]);
  }
  return [...groups.entries()].sort(([a], [b]) => (a === '' ? -1 : b === '' ? 1 : a.localeCompare(b)));
}
